import type { PriceList, Supplier } from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { deleteRow, updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { SupplierModal } from "./SupplierModal";

interface SupplierRow extends Supplier {
  productCount: number;
  userCount: number;
  locationCount: number;
}

/**
 * Suppliers, with counts of products, contacts and branches.
 *
 * `product_suppliers`, `supplier_contacts` and `supplier_locations` are still
 * empty (open item 3 in docs/OPEN-QUESTIONS.md), so where there is no live link
 * the page falls back to the counts that came across in the import — which is
 * what the old system showed.
 */
export function SuppliersTool() {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(60);
  const [error, setError] = useState<string | null>(null);
  /** The pop-up: "new" to add a supplier, or the one being edited. */
  const [editing, setEditing] = useState<SupplierRow | "new" | null>(null);

  const query = useAsync(async () => {
    const [suppliers, products, contacts, locations] = await Promise.all([
      supabase.from("suppliers").select("*").order("name"),
      supabase.from("product_suppliers").select("supplier_id"),
      supabase.from("supplier_contacts").select("supplier_id"),
      supabase.from("supplier_locations").select("supplier_id"),
    ]);

    const tally = (rows: { supplier_id: string }[]) => {
      const counts = new Map<string, number>();
      for (const row of rows)
        counts.set(row.supplier_id, (counts.get(row.supplier_id) ?? 0) + 1);
      return counts;
    };

    const byProduct = tally(unwrap(products));
    const byUser = tally(unwrap(contacts));
    const byLocation = tally(unwrap(locations));

    return unwrap<Supplier[]>(suppliers).map<SupplierRow>((supplier) => ({
      ...supplier,
      productCount: byProduct.get(supplier.id) ?? supplier.ext_products ?? 0,
      userCount: byUser.get(supplier.id) ?? supplier.ext_users ?? 0,
      locationCount: byLocation.get(supplier.id) ?? supplier.ext_locations ?? 0,
    }));
  }, []);

  const lists = useAsync(
    async () =>
      unwrap<PriceList[]>(
        await supabase.from("price_lists").select("*").order("name"),
      ),
    [],
  );

  const all = query.data ?? [];
  const filtered = search
    ? all.filter((s) =>
        `${s.name} ${s.abbr ?? ""} ${s.primary_supplier_contact ?? ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : all;
  const shown = filtered.slice(0, limit);

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <ErrorNote error={error} />
      <div className="toolbar2">
        <input
          placeholder={t("Search…")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setLimit(60);
          }}
        />
        <span className="count">
          {tn(filtered.length, "1 supplier", "{n} suppliers")}
          {filtered.length > shown.length
            ? t(" · showing {n}", { n: shown.length })
            : ""}
        </span>
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          onClick={() => setEditing("new")}
        >
          {t("+ Create")}
        </button>
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Name")}</th>
              <th>{t("Abbr")}</th>
              <th className="num">{t("Products")}</th>
              <th className="num">{t("Users")}</th>
              <th className="num">{t("Locations")}</th>
              <th>{t("Price sheet")}</th>
              <th>{t("On account")}</th>
              <th>{t("Is active")}</th>
              <th>{t("Primary supplier contact")}</th>
              <th>{t("Order email")}</th>
              <th>{t("QM credit")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={12} className="tnone">
                  {t("No suppliers.")}
                </td>
              </tr>
            ) : (
              shown.map((supplier) => (
                <tr key={supplier.id}>
                  <td>
                    <span className="lockedval" style={{ minWidth: 180 }}>
                      {supplier.name}
                    </span>
                  </td>
                  <td>
                    <ToolInput
                      value={supplier.abbr}
                      width={110}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, { abbr: v }),
                        )
                      }
                    />
                  </td>
                  <td className="num">
                    {t("{n} Products", { n: supplier.productCount })}
                  </td>
                  <td className="num">
                    {t("{n} Users", { n: supplier.userCount })}
                  </td>
                  <td className="num">
                    {t("{n} locations", { n: supplier.locationCount })}
                  </td>
                  <td>
                    <select
                      value={supplier.price_list_id ?? ""}
                      onChange={(e) =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, {
                            price_list_id: e.target.value || null,
                          }),
                        )
                      }
                    >
                      <option value="">{t("Catalogue cost")}</option>
                      {(lists.data ?? []).map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className={`tog green${supplier.on_account ? " on" : ""}`}
                      onClick={() =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, {
                            on_account: !supplier.on_account,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className={`tog green${supplier.active ? " on" : ""}`}
                      onClick={() =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, {
                            active: !supplier.active,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <ToolInput
                      value={supplier.primary_supplier_contact}
                      width={150}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, {
                            primary_supplier_contact: v,
                          }),
                        )
                      }
                    />
                  </td>
                  {/* Where a material order is emailed. Blank means the rep
                      types an address in every time. */}
                  <td>
                    <ToolInput
                      value={supplier.email}
                      width={190}
                      onSave={(v) =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, { email: v }),
                        )
                      }
                    />
                  </td>
                  {/* Heely-Brown credits our account for a QuickMeasure report
                      when the material is bought from them (ADR 0110). On here
                      is what puts the request in the order email. */}
                  <td>
                    <button
                      className={`tog green${
                        supplier.credits_quickmeasure ? " on" : ""
                      }`}
                      onClick={() =>
                        void guard(() =>
                          updateRow("suppliers", supplier.id, {
                            credits_quickmeasure:
                              !supplier.credits_quickmeasure,
                          }),
                        )
                      }
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      className="btn-small"
                      onClick={() => setEditing(supplier)}
                    >
                      Edit
                    </button>{" "}
                    <button
                      className="xbtn"
                      onClick={() => {
                        if (!window.confirm(t("Delete this supplier?"))) return;
                        void guard(() => deleteRow("suppliers", supplier.id));
                      }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > shown.length && (
        <button
          className="addrow ghost"
          onClick={() => setLimit((l) => l + 60)}
        >
          {t("Show more")}
        </button>
      )}

      {editing && (
        <SupplierModal
          supplier={editing === "new" ? undefined : editing}
          // Every supplier, not just the ones the search shows: names are
          // unique, and the one in the way may be switched off further down.
          existingNames={all.map((row) => row.name)}
          priceLists={lists.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={query.reload}
        />
      )}

      <div className="na-hint" style={{ marginTop: 10 }}>
        {t(
          "Supplier names cannot be changed — material orders reference them. Deactivate instead. On account means the company is invoiced here rather than paying at the till, so their purchases never appear in Receipts — the cost already reaches the job as a material order. Order email is where Place Material Order sends to. QM credit means this supplier credits our account for a QuickMeasure report when we buy the material from them; the order email asks for it. Counts fall back to the imported figures until products, contacts and branches are linked here. The price sheet decides what this supplier's materials cost when an estimate is generated; suppliers on the same sheet are quoted alike.",
        )}
      </div>
    </>
  );
}

export function ToolInput({
  value,
  width,
  onSave,
}: {
  value: string | null | undefined;
  width?: number;
  onSave: (value: string | null) => void;
}) {
  const initial = value ?? "";
  const [local, setLocal] = useState(initial);
  const [dirty, setDirty] = useState(false);
  if (!dirty && initial !== local) setLocal(initial);

  return (
    <input
      value={local}
      style={width ? { minWidth: width } : undefined}
      onChange={(e) => {
        setLocal(e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        onSave(local === "" ? null : local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
